import { useState } from 'react'
import { BarcodeDetector } from 'barcode-detector/ponyfill'
import { useDevices } from '@yudiel/react-qr-scanner'
import { Camera, ImagePlus, SwitchCamera } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Modal } from '@/components/ui/modal'
import { LiveBarcode } from '@/components/ui/live-barcode'
import { normalizeBarcode } from '@/lib/barcode'

interface BarcodeScannerProps {
  isOpen: boolean
  onClose: () => void
  onScan: (result: string) => void
}

export function BarcodeScanner({ isOpen, onClose, onScan }: BarcodeScannerProps) {
  const devices = useDevices()
  const [deviceId, setDeviceId] = useState<string | undefined>()
  const [error, setError] = useState<string | null>(null)
  const [manualCode, setManualCode] = useState('')
  const [session, setSession] = useState(0)

  if (!isOpen) return null

  const deliver = (raw: string) => {
    const code = normalizeBarcode(raw)
    if (!code) {
      setError('Enter a QR code or serial number.')
      return
    }
    onScan(code)
  }

  const switchCamera = () => {
    if (devices.length < 2) return
    const currentIndex = devices.findIndex((item) => item.deviceId === deviceId)
    const next = devices[(currentIndex + 1) % devices.length]
    setError(null)
    setDeviceId(next.deviceId)
    setSession((value) => value + 1)
  }

  const decodeImage = async (file: File) => {
    setError(null)
    const bitmap = await createImageBitmap(file)
    try {
      let codes = await new BarcodeDetector({ formats: ['qr_code', 'data_matrix'] }).detect(bitmap)
      if (!codes[0]?.rawValue) {
        codes = await new BarcodeDetector({ formats: ['any'] }).detect(bitmap)
      }
      const value = codes[0]?.rawValue
      if (!value) {
        setError('No QR code was found in that image.')
        return
      }
      deliver(value)
    } catch {
      setError('No QR code was found in that image.')
    } finally {
      bitmap.close()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={onClose} title="Scan QR code" size="lg">
      <div className="space-y-4">
        {error && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/10 px-3 py-2 text-sm text-destructive">
            {error}
          </div>
        )}

        <LiveBarcode
          key={`${session}-${deviceId || 'default'}`}
          className="h-72 w-full overflow-hidden rounded-xl bg-black"
          deviceId={deviceId}
          onError={setError}
          onDetect={deliver}
        />
        <p className="text-xs text-muted-foreground">
          Tap the code if it looks soft. The camera refocuses there, then reads the frame.
        </p>

        <div className="flex flex-wrap gap-2">
          {devices.length > 1 && (
            <Button type="button" variant="outline" onClick={switchCamera}>
              <SwitchCamera className="mr-2 h-4 w-4" />
              Switch camera
            </Button>
          )}
          <Button type="button" variant="outline" onClick={() => document.getElementById('barcode-photo')?.click()}>
            <ImagePlus className="mr-2 h-4 w-4" />
            Upload photo
          </Button>
          <input
            id="barcode-photo"
            type="file"
            accept="image/*"
            capture="environment"
            className="hidden"
            onChange={(event) => {
              const file = event.target.files?.[0]
              event.target.value = ''
              if (file) void decodeImage(file)
            }}
          />
        </div>

        <div className="flex gap-2">
          <Input
            value={manualCode}
            placeholder="Serial number"
            onChange={(event) => setManualCode(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                deliver(manualCode)
              }
            }}
          />
          <Button type="button" onClick={() => deliver(manualCode)}>
            <Camera className="mr-2 h-4 w-4" />
            Use code
          </Button>
        </div>
      </div>
    </Modal>
  )
}
